import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import { Menu } from 'lucide-react';
import Logo from '../ui/logo';
import { useAppShell } from '../layout/useAppShell';
import { studioBOn } from '../../lib/studioB/flag';
import { fadeUp, AnimatedValue, AnimatedBar } from './anim';

/**
 * Chrome for the analytics dashboard: a sticky 4K Labs header + its own
 * scroll container. The app shell sets `#root { overflow:hidden }`, so each
 * analytics page must scroll internally (flex column → scrollable <main>).
 */
export function AnalyticsShell({
  children,
  crumb,
  actions,
}: {
  children: ReactNode;
  /** Right-of-logo breadcrumb, e.g. a video title on the detail page. */
  crumb?: ReactNode;
  /** Optional right-aligned header controls. */
  actions?: ReactNode;
}) {
  const shell = useAppShell();
  return (
    <div className="flex h-full flex-col bg-background text-foreground">
      <header className={studioBOn() ? 'sticky top-0 z-20 studio-topbar' : 'sticky top-0 z-20 border-b border-border/60 bg-background/80 backdrop-blur-md'}>
        <div className="mx-auto flex h-14 max-w-7xl items-center gap-3 px-6">
          {shell?.isPhone && (
            <button type="button" onClick={shell.openNav} title="Open menu" className="-ml-2 grid h-9 w-9 place-items-center rounded-lg hover:bg-accent">
              <Menu className="h-5 w-5 text-muted-foreground" />
            </button>
          )}
          {(shell ? shell.logoInBar : true) && (
            <Link to="/" search={{ slide: undefined }} title="Home" className="flex items-center">
              <Logo variant="compact" className="shrink-0" />
            </Link>
          )}
          <Link to="/analytics" className="flex items-center gap-2.5">
            <span className="hidden text-sm font-semibold tracking-tight text-foreground/90 sm:inline">
              Analytics
            </span>
          </Link>
          {crumb && (
            <>
              <span className="text-border">/</span>
              <div className="min-w-0 flex-1 truncate text-sm text-muted-foreground">{crumb}</div>
            </>
          )}
          <div className="ml-auto flex items-center gap-2">{actions}</div>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-7xl px-6 py-8">{children}</div>
      </main>
    </div>
  );
}

/** A small, elegant labelled stat. */
export function StatCard({
  label,
  value,
  hint,
  accent,
  bar,
}: {
  label: string;
  value: string | number;
  hint?: string;
  accent?: boolean;
  /** Optional 0–100 progress bar under the value (e.g. completion). */
  bar?: number;
}) {
  return (
    <motion.div
      variants={fadeUp}
      transition={{ type: 'spring', stiffness: 400, damping: 26 }}
      className="rounded-lg border border-border/60 bg-card px-5 py-4"
    >
      <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{label}</div>
      <div
        className={`mt-2 text-[1.75rem] font-semibold leading-none tracking-tight tabular-nums ${
          accent ? 'text-primary' : 'text-foreground'
        }`}
      >
        <AnimatedValue value={value} />
      </div>
      {bar != null && <div className="mt-3"><AnimatedBar pct={bar} /></div>}
      {hint && <div className="mt-1.5 text-xs text-muted-foreground">{hint}</div>}
    </motion.div>
  );
}

/** A compact, dense metric strip — one bordered row, divided cells. */
export function MetricStrip({
  items,
  className = '',
}: {
  items: Array<{ label: string; value: string | number; accent?: boolean }>;
  className?: string;
}) {
  return (
    <div className={`flex flex-wrap overflow-hidden rounded-lg border border-border/60 bg-card ${className}`}>
      {items.map((m, i) => (
        <div
          key={i}
          className="min-w-[7rem] flex-1 px-4 py-3 [&:not(:first-child)]:border-l [&:not(:first-child)]:border-border/60"
        >
          <div className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">{m.label}</div>
          <div
            className={`mt-1 text-xl font-semibold leading-none tracking-tight tabular-nums ${
              m.accent ? 'text-primary' : 'text-foreground'
            }`}
          >
            <AnimatedValue value={m.value} />
          </div>
        </div>
      ))}
    </div>
  );
}

/** A titled content section with consistent card chrome. */
export function Section({
  title,
  subtitle,
  right,
  children,
}: {
  title: string;
  subtitle?: string;
  right?: ReactNode;
  children: ReactNode;
}) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      className="overflow-hidden rounded-xl border border-border/60 bg-card shadow-sm"
    >
      <div className="flex items-center justify-between gap-3 border-b border-border/60 px-5 py-3.5">
        <div>
          <h2 className="text-sm font-semibold tracking-tight">{title}</h2>
          {subtitle && <p className="mt-0.5 text-xs text-muted-foreground">{subtitle}</p>}
        </div>
        {right}
      </div>
      <div className="p-5">{children}</div>
    </motion.section>
  );
}
